import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { Check, X } from "lucide-react";
import styles from "../../../_css/AddRoleplay.module.css";
import { addCategories, getCategories, getSubCategories } from "../../../requests.js";
import CustomDropdown from "../../common/CustomDropdown.js";
import { notifyError, notifyInfo, notifySuccess } from "../../helper/Toaster.js";

// Define available languages (You can fetch this from API or keep static)
const LANGUAGES = [
    { label: "English", value: "English" },
    { label: "Dutch", value: "Dutch" },
    { label: "Flemish", value: "Flemish" },
    { label: "French", value: "French" },
];

const RoleSelection = ({
    handleNext,
    handleBack,
    step = 1,
    previousSelections = {},
}) => {
    // --- State Management ---

    // 1. Language State
    const [language, setLanguage] = useState("");

    const [goal, setGoal] = useState("");
    const [situation, setSituation] = useState("");

    // Categories
    const [categories, setCategories] = useState([]);
    const [categoryId, setCategoryId] = useState("");
    const [categoryName, setCategoryName] = useState("");
    const [addingCategory, setAddingCategory] = useState(false);
    const [newCategory, setNewCategory] = useState("");

    // Sub-categories
    const [subcategories, setSubcategories] = useState([]);
    const [subcategory, setSubcategory] = useState("");
    const [addingSubCategory, setAddingSubCategory] = useState(false);
    const [newSubCategory, setNewSubCategory] = useState("");
    const [isLoadingSub, setIsLoadingSub] = useState(false);

    // Is Saving
    const [isSaving, setIsSaving] = useState(false);

    const [isButtonEnabled, setIsButtonEnabled] = useState(false);

    const countCharacters = (text) => (text ? text.length : 0);

    // --- Effects ---

    // 1. Load Previous Selections & Language from LocalStorage
    useEffect(() => {
        const prev = previousSelections[`step${step}`];

        // Check Local Storage for Language first
        const storedLang = localStorage.getItem("selected_language");
        if (storedLang) {
            setLanguage(storedLang);
        }

        if (prev) {
            let parsed = prev;
            try {
                parsed = typeof prev === "string" ? JSON.parse(prev) : prev;
            } catch (e) { }

            setSituation(parsed.user_role || "");
            setGoal(parsed.avatar_role || "");
            setCategoryId(parsed.category_id || "");
            setCategoryName(parsed.category_name || "");
            setSubcategory(parsed.subcategory || "");

            // If previous selection has language, it overrides local storage
            if (parsed.selected_language) {
                setLanguage(parsed.selected_language);
                localStorage.setItem("selected_language", parsed.selected_language);
            }
        }
    }, [previousSelections, step]);

    // 2. Fetch Categories (Depends on Language)
    useEffect(() => {
        const fetchCategories = async () => {
            // Only fetch if language is selected
            if (!language) {
                setCategories([]);
                return;
            }

            try {
                // Sending language as payload
                const res = await getCategories({ selected_language: language });
                setCategories(res?.categories || res || []);
            } catch (err) {
                console.error("Error loading categories", err);
            }
        };
        fetchCategories();
    }, [language]); // Re-fetch if language changes

    // 3. Fetch Sub-Categories (Depends on Category & Language)
    useEffect(() => {
        const fetchSubs = async () => {
            setSubcategories([]);
            setSubcategory("");
            setAddingSubCategory(false);

            if (!categoryId || !language) return; // Guard clause

            setIsLoadingSub(true);
            try {
                // Sending language as payload
                const res = await getSubCategories({ categoryId, language });
                setSubcategories(res?.data || []);
            } catch (err) {
                console.error("Failed to fetch subcategories", err);
                setSubcategories([]);
            } finally {
                setIsLoadingSub(false);
            }
        };
        fetchSubs();
    }, [categoryId, language]); // Re-fetch if category or language changes

    // 4. Validate Next Button
    useEffect(() => {
        setIsButtonEnabled(
            language !== "" && // Check language
            situation.trim() !== "" &&
            goal.trim() !== "" &&
            categoryId !== "" &&
            subcategory !== ""
        );
    }, [situation, goal, categoryId, subcategory, language]);


    // --- Handlers ---

    const handleLanguageChange = (val) => {
        setLanguage(val);
        localStorage.setItem("selected_language", val);

        // Explain how localized roleplay data is saved.
        if (val === "Dutch" || val === "Flemish") {
            notifyInfo("All the data will be saved in the selected language.");
        }

        // Optional: Reset categories if language changes to ensure consistency
        setCategoryId("");
        setCategoryName("");
        setSubcategory("");
    };

    // Add New Category (API)
    const handleAddCategory = async () => {
        const trimmed = newCategory.trim();
        if (!trimmed) return;

        try {
            // Sending language as payload
            const res = await addCategories(trimmed, language);
            if (res && res.category) {
                notifySuccess(res.message || "Category created successfully");

                const newCat = res.category;
                setCategories((prev) => [...prev, newCat]);
                setCategoryId(newCat.id);
                setCategoryName(newCat.name);

                setAddingCategory(false);
                setNewCategory("");
            } else {
                notifyError("Failed to verify category creation.");
            }
        } catch (err) {
            console.error("Error adding category", err);
            notifyError(err?.message || "Error adding category");
        }
    };

    const handleCancelAdd = () => {
        setAddingCategory(false);
        setNewCategory("");
    };

    // Add New Sub-Category (Local Only)
    const handleAddSubCategory = () => {
        const value = newSubCategory.trim();
        if (!value) return;

        const tempSub = {
            name: value,
            value: value,
            id: `temp-${Date.now()}`
        };

        setSubcategories((prev) => [...prev, tempSub]);
        setSubcategory(value);

        setAddingSubCategory(false);
        setNewSubCategory("");
    };

    const handleCancelAddSub = () => {
        setAddingSubCategory(false);
        setNewSubCategory("");
    };

    const handleNextClick = async() => {
        if (!isButtonEnabled) return;

        setIsSaving(true);
        const res = await handleNext({
            selected_language: language, // Sending language in final payload
            category_id: Number(categoryId),
            category_name: categoryName,
            user_role: situation,
            avatar_role: goal,
            subcategory: subcategory,
        });
        setIsSaving(false);
    };

    // --- Render ---
    return (
        <div className={styles.stepCard}>
            {/* Header */}
            <div className={styles.headerContainer}>
                <div>
                    <h4 className={styles.title}>Define The Roles</h4>
                    <p className={styles.subHeader}>
                        Select a language first, then describe the roles and context.
                    </p>
                </div>
            </div>

            {/* 1. Language Selection */}
            <div className={styles.formGroup}>
                <label className={styles.label}>
                    Language <span className={styles.required}>*</span>
                </label>
                <CustomDropdown
                    value={language}
                    options={LANGUAGES}
                    onChange={handleLanguageChange}
                    label="Select Language"
                />
            </div>

            <hr className={styles.divider} />

            {/* 2. Category Section - Disabled if no language */}
            <div className={styles.formGroup}>
                <label className={styles.label}>
                    Category <span className={styles.required}>*</span>
                </label>

                {!addingCategory ? (
                    <>
                        <CustomDropdown
                            value={categoryId}
                            options={categories.map((c) => ({
                                label: c.label || c.name,
                                value: c.value || c.id,
                            }))}
                            onChange={(val) => {
                                const cat = categories.find((c) => (c.value || c.id) === Number(val));
                                setCategoryId(val);
                                setCategoryName(cat?.label || cat?.name || "");
                            }}
                            label="Select Category"
                            disabled={!language} // Disable logic
                        />
                        <button
                            type="button"
                            className={styles.linkBtn}
                            onClick={() => setAddingCategory(true)}
                            disabled={!language} // Disable logic
                            style={{ opacity: !language ? 0.5 : 1, pointerEvents: !language ? 'none' : 'auto' }}
                        >
                            + Add new category
                        </button>
                    </>
                ) : (
                    <div className={styles.inputWrapper}>
                        <input
                            className={`form-control ${styles.inlineInput}`}
                            placeholder="Enter new category name..."
                            value={newCategory}
                            onChange={(e) => setNewCategory(e.target.value)}
                            autoFocus
                        />
                        <div className={styles.iconActions}>
                            <button
                                className={`${styles.iconBtn} ${styles.saveIcon}`}
                                onClick={handleAddCategory}
                                title="Save Category"
                            >
                                <Check size={20} />
                            </button>
                            <button
                                className={`${styles.iconBtn} ${styles.cancelIcon}`}
                                onClick={handleCancelAdd}
                                title="Cancel"
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* 3. Sub-Category Section - Disabled if no language or no category */}
            <div className={styles.formGroup}>
                <label className={styles.label}>
                    Sub-Category <span className={styles.required}>*</span>
                </label>

                {!addingSubCategory ? (
                    <>
                        <CustomDropdown
                            value={subcategory}
                            options={subcategories.map((s) => ({
                                label: s.value || s.name,
                                value: s.value || s.name,
                            }))}
                            onChange={(val) => setSubcategory(val)}
                            label="Select Sub-Category"
                            disabled={addingCategory || !categoryId || !language} // Disable logic
                            isLoading={isLoadingSub}
                        />
                        <button
                            type="button"
                            className={styles.linkBtn}
                            onClick={() => setAddingSubCategory(true)}
                            disabled={addingCategory || !categoryId || !language} // Disable logic
                            style={{
                                opacity: (addingCategory || !categoryId || !language) ? 0.5 : 1,
                                pointerEvents: (addingCategory || !categoryId || !language) ? 'none' : 'auto'
                            }}
                        >
                            + Add new sub-category
                        </button>
                    </>
                ) : (
                    <div className={styles.inputWrapper}>
                        <input
                            className={`form-control ${styles.inlineInput}`}
                            placeholder="Enter new sub-category..."
                            value={newSubCategory}
                            onChange={(e) => setNewSubCategory(e.target.value)}
                            autoFocus
                        />
                        <div className={styles.iconActions}>
                            <button
                                className={`${styles.iconBtn} ${styles.saveIcon}`}
                                onClick={handleAddSubCategory}
                                title="Add Sub-Category"
                            >
                                <Check size={20} />
                            </button>
                            <button
                                className={`${styles.iconBtn} ${styles.cancelIcon}`}
                                onClick={handleCancelAddSub}
                                title="Cancel"
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* 4. User Role Input - Disabled if no language */}
            <div className={styles.formGroup}>
                <div className={styles.labelRow}>
                    <label className={styles.label}>
                        Your role (short): <span className={styles.required}>*</span>
                    </label>
                    <span className={styles.charCount}>
                        Characters:{countCharacters(situation)}/150
                    </span>
                </div>
                <textarea
                    className={`${styles.textArea} form-control`}
                    placeholder="e.g. Sales Manager negotiating a new contract..."
                    value={situation}
                    onChange={(e) => setSituation(e.target.value)}
                    rows={1}
                    disabled={!language} // Disable logic
                />
            </div>

            {/* 5. Avatar Role Input - Disabled if no language */}
            <div className={styles.formGroup}>
                <div className={styles.labelRow}>
                    <label className={styles.label}>
                        Other person’s role (short): <span className={styles.required}>*</span>
                    </label>
                    <span className={styles.charCount}>
                        Characters:{countCharacters(goal)}/150
                    </span>
                </div>
                <textarea
                    className={`${styles.textArea} form-control`}
                    placeholder="e.g. A hesitant client concerned about budget..."
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    rows={1}
                    disabled={!language} // Disable logic
                />
            </div>

            {/* Navigation Buttons */}
            <div className={styles.buttonGroup}>
                {step > 1 && (
                    <Button
                        variant="light"
                        className={styles.backButton}
                        onClick={handleBack}
                    >
                        Back
                    </Button>
                )}
                <Button
                    className={styles.nextButton}
                    disabled={!isButtonEnabled || isSaving}
                    onClick={handleNextClick}
                >
                    {isSaving ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Saving...
                        </>
                    ) : "Next"}
                </Button>
            </div>
        </div>
    );
};

export default RoleSelection;